import { createContext, useContext, useReducer } from 'react';

const AppContext = createContext();

// Estado inicial
const initialState = {
  // Datos
  currentMetrics: null,
  tickets: [],
  projects: [],
  projectManagers: [],
  agents: [],
  ticketTypes: [],
  ticketStates: [],
  stateMachines: null, // estados posibles por tipo de ticket (GET /state-machines)
  teams: [],

  // Filtros
  filters: {
    ticketNumber: null,
    startDate: null,
    endDate: null,
    organizationId: null,
    ownerId: null,
    teamId: null,
    state: null,
    projectManager: null,
    type: 'Incident' // Filtro por defecto
  },

  // UI State
  loading: false,
  error: null,
  toasts: [],

  // Paginación
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalPages: 0
  },

  // Ordenamiento
  sorting: {
    field: null,
    direction: 'asc' // 'asc' | 'desc'
  },

  // Modal
  modal: {
    isOpen: false,
    ticketNumber: null,
    data: null
  }
};

// Reducer para manejar acciones
function appReducer(state, action) {
  switch (action.type) {
    // ==================== DATOS ====================
    case 'SET_METRICS':
      return {
        ...state,
        currentMetrics: action.payload,
        loading: false,
        error: null
      };

    case 'SET_TICKETS':
      return {
        ...state,
        tickets: action.payload,
        loading: false,
        error: null
      };

    case 'SET_PROJECTS':
      return {
        ...state,
        projects: action.payload
      };

    case 'SET_PROJECT_MANAGERS':
      return {
        ...state,
        projectManagers: action.payload
      };

    case 'SET_AGENTS':
      return {
        ...state,
        agents: action.payload
      };

    case 'SET_TICKET_TYPES':
      return {
        ...state,
        ticketTypes: action.payload
      };

    case 'SET_TICKET_STATES':
      return {
        ...state,
        ticketStates: action.payload
      };

    case 'SET_STATE_MACHINES':
      return {
        ...state,
        stateMachines: action.payload
      };

    case 'SET_TEAMS':
      return {
        ...state,
        teams: action.payload
      };

    // ==================== FILTROS ====================
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, currentPage: 1 } // Reset page
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {
          ...initialState.filters,
          // Mantener fechas y tipo por defecto al resetear
          startDate: state.filters.startDate,
          endDate: state.filters.endDate,
          type: 'Incident'
        },
        pagination: { ...state.pagination, currentPage: 1 }
      };

    // ==================== UI STATE ====================
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    // ==================== PAGINACIÓN ====================
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };

    case 'SET_PAGE':
      return {
        ...state,
        pagination: { ...state.pagination, currentPage: action.payload }
      };

    case 'SET_PAGE_SIZE':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          pageSize: action.payload,
          currentPage: 1 // Reset to first page
        }
      };

    // ==================== ORDENAMIENTO ====================
    case 'SET_SORTING':
      return {
        ...state,
        sorting: action.payload
      };

    case 'TOGGLE_SORT':
      const currentField = state.sorting.field;
      const currentDirection = state.sorting.direction;

      return {
        ...state,
        sorting: {
          field: action.payload,
          direction: currentField === action.payload && currentDirection === 'asc'
            ? 'desc'
            : 'asc'
        }
      };

    // ==================== MODAL ====================
    case 'OPEN_MODAL':
      return {
        ...state,
        modal: {
          isOpen: true,
          ticketNumber: action.payload.ticketNumber,
          data: action.payload.data || null
        }
      };

    case 'CLOSE_MODAL':
      return {
        ...state,
        modal: {
          isOpen: false,
          ticketNumber: null,
          data: null
        }
      };

    // ==================== TOASTS ====================
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, { id: Date.now(), ...action.payload }]
      };

    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(t => t.id !== action.payload)
      };

    default:
      return state;
  }
}

// Provider Component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

// Hook personalizado para usar el contexto
export function useApp() {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useApp debe usarse dentro de AppProvider');
  }

  return context;
}

export default AppContext;
